import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { usePageMeta } from "../../components/Layout";
import {
    ArrowLeft, ChevronDown, ChevronRight, Check, Lock, AlertTriangle,
    Paperclip, Copy, Pencil, X as XIcon, Info,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

// Two-client model (Phase 1). Hidden behind an env flag so the
// "+ Add second client" button only appears once the operator has
// run the clients_v2 migrator on production.
const TWO_CLIENT_UI_ENABLED =
    (process.env.REACT_APP_TWO_CLIENT_UI || "").trim() === "1";
import { WS2ActiveModules } from "./B5Pages";
import { WS3ActiveModules } from "./B6Pages";
import { WS4ActiveModules } from "./B7aPages";
import { WS5ActiveModules } from "./B8Pages";
import { WS6ActiveModules } from "./B9B10Pages";
import { WS1ActiveModules } from "./B4Pages";
import { PostCompletionPage } from "./B13Pages";
import { ProjectStateRail, HoldDialog, ResumeDialog, CloseDialog, ReopenDialog } from "./B14ProjectStateRail";
import {
    Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from "../../components/ui/tooltip";

// Defensive renderer for `ws.next_action`. Historical data has at
// least two shapes in production:
//   • plain string (from the WS1 qualification modal — see B4Pages §544)
//   • object `{title: "..."}` (seed data + stage5_b4_ws1 updates)
// If a future shape slips through (`.label`, `.text`, `.summary`,
// `.description`), fall back to the first string-valued field rather
// than letting React stringify the whole object to `[object Object]`.
function renderNextAction(na) {
    if (!na) return null;
    if (typeof na === "string") {
        const t = na.trim();
        return t ? t : null;
    }
    if (typeof na === "object") {
        for (const key of ["title", "label", "text", "summary", "description"]) {
            const v = na[key];
            if (typeof v === "string" && v.trim()) return v;
        }
    }
    return null;
}

// D5 pre-deployment polish — one-line purpose statements shown in a
// small info tooltip beside each Workstage accordion header title.
// Copy is verbatim from the human's authorised source; do not
// paraphrase, translate, or truncate.
const WORKSTAGE_TOOLTIPS = {
    1: "Capture and qualify the initial enquiry, establish the client's requirements, make first contact, and decide whether the opportunity should progress.",
    2: "Assess whether the proposed project is viable, clarify key constraints and requirements, and establish whether we should proceed to the design stage.",
    3: "Onboard the client into the free design process, collect the information needed to begin, and complete the required agreement and setup steps.",
    4: "Develop and manage the free design package, coordinate client decisions and approvals, and establish the basis for progressing toward a construction proposal.",
    5: "Convert the agreed design into a construction-ready opportunity by completing the required surveys, technical work, pricing and pre-construction preparation.",
    6: "Manage the contracted project through paid design, pre-construction and construction services, including commercial, contractual, programme, change and payment matters.",
    7: "Manage the project after completion, including outstanding actions, handover matters, follow-up and the final close-out of the client relationship.",
};

// B2 — Canonical Project Workspace shell. §2–§27 of the B2 brief.
// The primary Workstage modules stack vertically (§10). The right rail
// order is locked: Project Summary → Actions → Communications →
// Attention Needed (§12, §17). WS1 workflow is NOT implemented here
// (§11 — B4 owns that).

// The seven locked Stage 5 Workstages. §7 overrides the reference:
// - NO "Paid Route" — Workstage 6 is "Paid Services"
// - NO Workstage 8 "Finalised" — Closed is Project State, not a stage.
const WORKSTAGES = [
    { num: 1, name: "Enquiry & Qualification" },
    { num: 2, name: "Feasibility" },
    { num: 3, name: "Free Design Onboarding" },
    { num: 4, name: "Free Design Package" },
    { num: 5, name: "Project Transition" },
    { num: 6, name: "Paid Services" },
    { num: 7, name: "Post Completion" },
];

const BUDGET_LABEL = {
    under_100k: "Under £100k",
    "100_150k": "£100–150k",
    "150_250k": "£150–250k",
    "250k_plus": "£250k+",
    guidance: "Need guidance",
};
const TIMING_LABEL = {
    asap: "ASAP",
    within_3m: "Within 3 months",
    "3_6m": "3–6 months",
    "6_12m": "6–12 months",
    more_than_12m: "More than 12 months",
};
const PROJECT_TYPE_LABEL = {
    rear_extension: "Rear extension",
    side_extension: "Side extension",
    loft: "Loft",
    internal_reconfiguration: "Internal reconfiguration",
    combination: "Combination",
    other: "Other",
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return "—"; }
}

function StateBadge({ state }) {
    const tone = state === "Active" ? "bg-[#E8F5E9] text-[#2E7D32]"
        : state === "On Hold" ? "bg-[#FFF3E0] text-[#E65100]"
        : "bg-[#EEE] text-[#616161]";
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-semibold ${tone}`}>{state}</span>;
}

function HeaderTag({ label, value, tone }) {
    return (
        <span className="inline-flex items-center gap-1.5 text-[13px]" data-testid={`workspace-header-tag-${label.toLowerCase().replace(/\s+/g,'-')}`}>
            <span className={`px-2 py-0.5 rounded ${tone || "bg-[#FDF4F2] text-terracotta"} text-[11px] font-manrope font-semibold uppercase tracking-wide`}>{label}</span>
            <span className="text-charcoal">{value || "—"}</span>
        </span>
    );
}

// Chevron-styled Workstage ribbon. Each segment is a chevron shape
// clipped with CSS `clip-path`, with a small consistent gap between
// tiles. Text is horizontally + vertically centred within each tile
// (labels may wrap to two lines). State is derived from the
// authoritative `workstage_states` payload — never from a raw stage-
// number comparison (§2 of the Final Corrections). The ribbon is
// orientation only: segments are non-focusable `role="listitem"`
// divs. No arbitrary stage-jumping is reachable.
//
// Palette (locked, §7): charcoal text on inactive tiles; WHITE bold
// text on the terracotta Active tile. Special states retain their
// prior tints.
const RIBBON_STATE_TO_CLASSES = {
    "Active":                  "bg-[#A8543F] text-white",
    "Completed":               "bg-[#F5F3EE] text-[#1D1D1B]",
    "Not Started":             "bg-[#F5F3EE] text-[#1D1D1B]/60",
    "Skipped / Not Entered":   "bg-[#F5F3EE] text-[#7A6A50] italic",
    "Ended by Project Closure":"bg-[#EDE3E1] text-[#7A5148]",
    "On Hold":                 "bg-[#FDF3E4] text-[#8A5A1F]",
};

function WorkstageRibbon({ workstageStates, projectState }) {
    // Chevron geometry: `POINT` is the horizontal depth of the arrow
    // point and inward notch. `GAP` is the visible seam between
    // adjacent chevrons — kept to 2 px so the seam reads as a hair-
    // line rather than a fat gap. The ribbon container's background
    // shows through the gap in a soft warm grey (`SEAM`).
    const POINT = 7;
    const GAP = 0.5;
    const SEAM = "#D9D5CC";
    return (
        <div
            className="flex flex-nowrap overflow-x-auto border border-[#E5E2DC] rounded-lg p-1"
            style={{ backgroundColor: SEAM }}
            data-testid="workspace-workstage-ribbon"
            role="list"
            aria-label="Workstage progress">
            {WORKSTAGES.map(({ num, name }, idx) => {
                const s = (workstageStates || []).find(x => x.workstage === num) || { state: "Not Started" };
                // §1 Correction — the ribbon must not visually imply a
                // project is actively progressing when Project State
                // is On Hold or Closed.  We derive `presentedState`
                // from BOTH the workstage record state AND the
                // authoritative Project State projection.  The
                // underlying record state is still exposed via
                // `data-record-state` for testability.
                const recordState = s.state;
                let presentedState = recordState;
                if (recordState === "Active" && projectState === "On Hold") {
                    presentedState = "On Hold";
                } else if (recordState === "Active" && projectState === "Closed") {
                    presentedState = "Ended by Project Closure";
                }
                const isActive = presentedState === "Active";
                const isFuture = presentedState === "Not Started";
                const isCompleted = presentedState === "Completed";
                const isSkipped = presentedState === "Skipped / Not Entered";
                const isEndedByClosure = presentedState === "Ended by Project Closure";
                const isOnHold = presentedState === "On Hold";
                const isReopened = !!s.reopened;
                const tone = RIBBON_STATE_TO_CLASSES[presentedState] || RIBBON_STATE_TO_CLASSES[recordState] || RIBBON_STATE_TO_CLASSES["Not Started"];
                const isFirst = idx === 0;
                const isLast = idx === WORKSTAGES.length - 1;
                const clip = isFirst
                    ? `polygon(0 0, calc(100% - ${POINT}px) 0, 100% 50%, calc(100% - ${POINT}px) 100%, 0 100%)`
                    : isLast
                    ? `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${POINT}px 50%)`
                    : `polygon(0 0, calc(100% - ${POINT}px) 0, 100% 50%, calc(100% - ${POINT}px) 100%, 0 100%, ${POINT}px 50%)`;
                const marginLeft = isFirst ? 0 : GAP;
                const paddingLeft = isFirst ? 12 : POINT + 8;
                const paddingRight = isLast ? 12 : POINT + 8;
                // +1pt uniformly: eyebrow 10→11, label 12→13.
                // Active label also bold + white.
                return (
                    <div
                        key={num}
                        role="listitem"
                        aria-current={isActive ? "step" : undefined}
                        data-testid={`workstage-ribbon-step-${num}`}
                        data-status={
                            isActive ? "active"
                            : isCompleted ? "completed"
                            : isSkipped ? "skipped"
                            : isEndedByClosure ? "ended-by-closure"
                            : isOnHold ? "on-hold"
                            : "future"}
                        data-record-state={recordState}
                        data-reopened={isReopened ? "true" : "false"}
                        style={{
                            clipPath: clip,
                            WebkitClipPath: clip,
                            marginLeft,
                            paddingLeft,
                            paddingRight,
                            minHeight: 56,
                        }}
                        className={`relative flex-1 min-w-[140px] py-2 leading-tight select-none flex flex-col items-center justify-center text-center ${tone} ${isActive ? "shadow-sm" : ""}`}>
                        <div className={`flex items-center justify-center gap-1.5 font-manrope font-semibold text-[11px] uppercase tracking-wide ${isActive ? "opacity-100" : "opacity-80"}`}>
                            <span>WS {num}</span>
                            {isCompleted && <Check className="w-3 h-3" />}
                            {isFuture && <Lock className="w-3 h-3" />}
                            {isSkipped && <span className="text-[11px]">↷</span>}
                            {isEndedByClosure && <span className="text-[11px]">⛔</span>}
                            {isReopened && <span className="text-[11px] normal-case tracking-normal not-italic">· Reopened</span>}
                        </div>
                        <div className={`mt-0.5 text-[13px] not-italic ${isActive ? "font-semibold" : "font-medium"}`}>{name}</div>
                    </div>
                );
            })}
        </div>
    );
}

// A single Workstage accordion container. §9 architecture.
// - Active WS expanded by default
// - Completed WS collapsible; header may show a concise summary line
// - Future WS visible but locked (§9 — no arbitrary jumping)
// B2 does NOT populate the WS1 operational modules (§11 — B4 owns).
function WorkstageAccordion({ ws, name, status, statusLabel, expanded, onToggle, children }) {
    // Dev-support R2 §1/§4-6 — future WS accordions are now expandable
    // and rendered read-only.  All statuses (including future) toggle.
    const canToggle = true;
    const borderClass = status === "active" ? "border-charcoal"
        : status === "skipped" ? "border-[#E9DFCC]"
        : status === "ended-by-closure" ? "border-[#E5D1CB]"
        : "border-[#E5E2DC]";
    const tooltipCopy = WORKSTAGE_TOOLTIPS[ws];
    const handleHeaderKey = (e) => {
        if (!canToggle) return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle && onToggle();
        }
    };
    // The header is a `<div role="button">` rather than a native
    // `<button>` so we can nest a real focusable info-tooltip trigger
    // inside without producing invalid nested-button markup.  Keyboard
    // parity is preserved via `onKeyDown` (Enter/Space).
    return (
        <section
            data-testid={`workstage-accordion-${ws}`}
            data-status={status}
            className={`bg-white border rounded-lg ${borderClass}`}>
            <div
                role="button"
                tabIndex={canToggle ? 0 : -1}
                onClick={canToggle ? onToggle : undefined}
                onKeyDown={handleHeaderKey}
                aria-expanded={canToggle ? expanded : false}
                aria-disabled={!canToggle}
                data-testid={`workstage-accordion-header-${ws}`}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left select-none ${canToggle ? "cursor-pointer hover:bg-[#FAFAFA]" : "cursor-not-allowed"}`}>
                {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span className="font-manrope font-semibold text-charcoal text-[15px]">
                    Workstage {ws}: {name}
                </span>
                {tooltipCopy && (
                    <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                aria-label={`About Workstage ${ws}`}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                    // Prevent Enter/Space on the trigger
                                    // from also toggling the accordion.
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.stopPropagation();
                                    }
                                }}
                                data-testid={`workstage-accordion-info-${ws}`}
                                className="inline-flex items-center justify-center w-5 h-5 text-[#999] hover:text-charcoal focus:outline-none focus-visible:ring-1 focus-visible:ring-charcoal rounded-full">
                                <Info className="w-3.5 h-3.5" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent
                            side="bottom"
                            align="start"
                            className="max-w-[360px] bg-charcoal text-white text-[12px] leading-snug px-3 py-2 rounded-md font-normal normal-case tracking-normal"
                            data-testid={`workstage-accordion-info-tooltip-${ws}`}>
                            {tooltipCopy}
                        </TooltipContent>
                    </Tooltip>
                )}
                <span className="ml-auto text-[12px] uppercase tracking-wide font-manrope font-semibold text-[#666]" data-testid={`workstage-accordion-status-${ws}`}>
                    {statusLabel}
                </span>
            </div>
            {expanded && canToggle && (
                <div className="border-t border-[#E5E2DC] px-4 py-4 flex flex-col gap-4" data-testid={`workstage-accordion-content-${ws}`}>
                    {children}
                </div>
            )}
        </section>
    );
}


// Placeholder module for the shell. B4 replaces this with the real WS1
// operational modules. §10 rule: every primary module is FULL WIDTH
// and stacks vertically. This shell enforces that.
function ModulePlaceholder({ title, description, testid }) {
    return (
        <div className="w-full bg-[#FDFCFA] border border-dashed border-[#E5E2DC] rounded-md p-4" data-testid={testid}>
            <div className="font-manrope font-semibold text-[13px] uppercase tracking-wide text-[#666] mb-1">{title}</div>
            <div className="text-[13px] text-[#999]">{description}</div>
        </div>
    );
}

const PROPERTY_STATUS_LABEL = {
    own: "Owned",
    recently_purchased: "Recently purchased",
    currently_purchasing: "Currently purchasing",
};

// Compact copy-to-clipboard control used beside contact / property
// fields in the Project Summary. Non-editing — clicking only copies.
// A11y label per §5. Restrained confirmation via a "Copied" tag that
// auto-dismisses; no modal. Falls back to `document.execCommand` when
// the async Clipboard API is unavailable.
function CopyButton({ value, label, testid }) {
    const [copied, setCopied] = useState(false);
    const doCopy = useCallback(async () => {
        const text = String(value ?? "");
        if (!text) return;
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const ta = document.createElement("textarea");
                ta.value = text; document.body.appendChild(ta);
                ta.select(); document.execCommand("copy");
                document.body.removeChild(ta);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* copy failed — deliberately silent, control is non-critical */
        }
    }, [value]);
    return (
        <button
            type="button"
            onClick={doCopy}
            aria-label={label}
            title={label}
            disabled={!value}
            data-testid={testid}
            data-copied={copied ? "true" : "false"}
            className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border transition
                ${value ? "border-[#E5E2DC] text-[#666] hover:bg-[#FAFAFA]" : "border-transparent text-[#CCC] cursor-not-allowed"}`}>
            {copied ? (
                <><Check className="w-3 h-3" /><span>Copied</span></>
            ) : (
                <Copy className="w-3 h-3" />
            )}
        </button>
    );
}

// Read-only Address value with an "Edit" affordance. Save persists to
// the workspace endpoint via PATCH; Cancel discards. Only the
// `property_address` and `full_name` are editable from the Project
// Summary — no other field is mutable via this component.
// ---------------------------------------------------------------------------
// Two-client model (Phase 1) — client identity editors.
//
// Plan reference: /app/memory/implementation_plan_two_clients.md §7.1–7.3.
// Replaces the legacy `ClientNameEditor` / `ClientEmailEditor` / `PhoneEditor`
// trio with a unified `ClientBlock` that renders four per-slot inline
// editors (FirstName, LastName, Email, Phone) and takes a `clientIdx`
// (0 or 1).  The same component powers Client 01 (always rendered) and
// Client 02 (rendered when `clients.length === 2`).
//
// All writes hit `/api/admin/projects/{pid}/clients/{idx}/{field}` with the
// shared `ClientFieldUpdate` body (`value`, `expected_record_version`,
// `create_op_id`).  The backend mirrors every clients[0] write back to
// the legacy `full_name` / `email` / `phone` roots (invariant 1), so
// unmigrated readers continue to see fresh data.
//
// Legacy backend endpoints `/full_name`, `/email`, `/phone` remain live
// via delegation and are kept for any out-of-band callers; no frontend
// surface in this repo targets them any more.
// ---------------------------------------------------------------------------

const _EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function _mkOpId(prefix) {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Single-field inline editor for `clients[idx].{field}`.
 *
 * Props:
 *   - projectId:        enquiry/project id
 *   - clientIdx:        0 (Client 01) | 1 (Client 02)
 *   - field:            "first_name" | "last_name" | "email" | "phone"
 *   - label:            human label used in error messages / aria
 *   - inputType:        html input type (default "text")
 *   - placeholder:      input placeholder
 *   - initialValue:     current server value
 *   - expectedVersion:  shared record_version (OCC)
 *   - onSaved:          ({ clients, record_version }) => void
 *   - allowEmpty:       true to accept empty-string (clears field); default true
 *   - validate:         optional (trimmed) => string | null error message
 *   - testidPrefix:     e.g. "client-0-first-name"
 */
function ClientFieldEditor({
    projectId, clientIdx, field, label,
    inputType = "text", placeholder,
    initialValue, expectedVersion,
    onSaved, allowEmpty = true, validate,
    testidPrefix,
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(initialValue || "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => { if (!editing) setDraft(initialValue || ""); },
        [initialValue, editing]);

    const start = () => {
        setDraft(initialValue || "");
        setEditing(true);
        setError(null);
    };
    const cancel = () => {
        setDraft(initialValue || "");
        setEditing(false);
        setError(null);
    };

    const save = async () => {
        const trimmed = (draft || "").trim();
        if (!allowEmpty && !trimmed) {
            setError(`${label} cannot be empty.`);
            return;
        }
        if (validate) {
            const v = validate(trimmed);
            if (v) { setError(v); return; }
        }
        setSaving(true); setError(null);
        try {
            const r = await api.patch(
                `/admin/projects/${projectId}/clients/${clientIdx}/${field}`,
                {
                    value: trimmed || null,
                    expected_record_version: expectedVersion,
                    create_op_id: _mkOpId(`c${clientIdx}-${field}`),
                },
            );
            onSaved && onSaved({
                clients: r.data?.clients || [],
                record_version: r.data?.record_version ?? (expectedVersion + 1),
            });
            setEditing(false);
        } catch (e) {
            const code = e?.response?.data?.detail?.code;
            if (code === "record_version_mismatch") {
                setError("The project changed since you loaded it. Reload and try again.");
            } else if (e?.response?.status === 422) {
                setError(`Enter a valid ${label.toLowerCase()}.`);
            } else {
                setError(`Could not save ${label.toLowerCase()}.`);
            }
        } finally { setSaving(false); }
    };

    const displayValue = initialValue && initialValue.toString().trim()
        ? initialValue
        : null;

    if (!editing) {
        return (
            <div className="flex items-start gap-1.5"
                 data-testid={`${testidPrefix}-read`}>
                <span className="break-words min-w-0"
                      data-testid={`${testidPrefix}-value`}>
                    {displayValue || <span className="text-[#999]">Not set</span>}
                </span>
                <button
                    type="button"
                    onClick={start}
                    aria-label={`Edit ${label.toLowerCase()}`}
                    title={`Edit ${label.toLowerCase()}`}
                    data-testid={`${testidPrefix}-edit`}
                    className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border border-[#E5E2DC] text-[#666] hover:bg-[#FAFAFA]">
                    <Pencil className="w-3 h-3" />
                </button>
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-1.5"
             data-testid={`${testidPrefix}-edit-form`}>
            <input
                type={inputType}
                data-testid={`${testidPrefix}-input`}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                    if (e.key === "Enter") { e.preventDefault(); save(); }
                    else if (e.key === "Escape") { cancel(); }
                }}
                placeholder={placeholder}
                className="w-full border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] outline-none focus:border-charcoal"
            />
            {error && <div className="text-[11px] text-terracotta"
                           data-testid={`${testidPrefix}-error`}>{error}</div>}
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid={`${testidPrefix}-save`}
                    className="text-[12px] px-2.5 py-1 rounded-md bg-charcoal text-white hover:bg-black transition disabled:opacity-60">
                    {saving ? "Saving…" : "Save"}
                </button>
                <button
                    type="button"
                    onClick={cancel}
                    disabled={saving}
                    data-testid={`${testidPrefix}-cancel`}
                    className="text-[12px] px-2.5 py-1 rounded-md border border-[#E5E2DC] text-charcoal hover:bg-[#FAFAFA] transition">
                    Cancel
                </button>
            </div>
        </div>
    );
}

/** FirstName editor — thin alias around ClientFieldEditor. */
function FirstNameEditor(props) {
    return <ClientFieldEditor
        field="first_name"
        label="First name"
        placeholder="First name"
        testidPrefix={`client-${props.clientIdx}-first-name`}
        {...props} />;
}

/** LastName editor — thin alias around ClientFieldEditor. */
function LastNameEditor(props) {
    return <ClientFieldEditor
        field="last_name"
        label="Last name"
        placeholder="Last name"
        testidPrefix={`client-${props.clientIdx}-last-name`}
        {...props} />;
}

/** Email editor — thin alias around ClientFieldEditor with format check. */
function ClientEmailEditor(props) {
    const validate = (trimmed) => {
        if (!trimmed) return null;   // allow clearing
        return _EMAIL_RE.test(trimmed.toLowerCase())
            ? null : "Enter a valid email address.";
    };
    return <ClientFieldEditor
        field="email"
        label="Email"
        inputType="email"
        placeholder="client@example.com"
        validate={validate}
        testidPrefix={`client-${props.clientIdx}-email`}
        {...props} />;
}

/** Phone editor — thin alias around ClientFieldEditor. */
function PhoneEditor(props) {
    return <ClientFieldEditor
        field="phone"
        label="Phone"
        inputType="tel"
        placeholder="+44 20 …"
        testidPrefix={`client-${props.clientIdx}-phone`}
        {...props} />;
}

/**
 * ClientBlock — renders the four per-slot inline editors for one client
 * slot (0 or 1).  Shows a "× Remove" affordance on Client 02 only.
 * The remove button opens a shadcn AlertDialog; the parent decides
 * what to do (see `onRemoveClient` in `ProjectWorkspacePage`).
 */
function ClientBlock({
    ws, clientIdx, client, onFieldSaved,
    onRemove, removeBusy,
}) {
    const projectId = ws?.id;
    const version = ws?.record_version || 0;
    const label = clientIdx === 0 ? "Client 01" : "Client 02";
    const [removeOpen, setRemoveOpen] = useState(false);
    const canRemove = clientIdx === 1 && typeof onRemove === "function";

    return (
        <div className={clientIdx === 0 ? "" : "mt-3"}
             data-testid={`client-block-${clientIdx}`}>
            <div className="flex items-center justify-between mb-1.5">
                <div className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666]"
                     data-testid={`client-block-${clientIdx}-label`}>
                    {label}
                </div>
                {canRemove && (
                    <>
                        <button
                            type="button"
                            onClick={() => setRemoveOpen(true)}
                            disabled={removeBusy}
                            data-testid={`remove-client-${clientIdx}-btn`}
                            className="text-[11px] px-1.5 py-0.5 rounded border border-[#E5E2DC] text-terracotta hover:bg-[#FAFAFA] disabled:opacity-60">
                            {removeBusy ? "Removing…" : "× Remove"}
                        </button>
                        <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
                            <AlertDialogContent
                                data-testid={`remove-client-${clientIdx}-dialog`}>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Remove Client 02?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        This removes the second client from the project.
                                        The change is permanent and audited; Client 01 is
                                        unaffected.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel
                                        data-testid={`remove-client-${clientIdx}-cancel`}>
                                        Cancel
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                        data-testid={`remove-client-${clientIdx}-confirm`}
                                        onClick={() => { setRemoveOpen(false); onRemove(); }}>
                                        Remove
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </>
                )}
            </div>
            <dl className="grid grid-cols-[130px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[13px]">
                <dt className="text-[#666]">First name:</dt>
                <dd className="min-w-0 text-charcoal break-words">
                    <FirstNameEditor
                        projectId={projectId}
                        clientIdx={clientIdx}
                        initialValue={client?.first_name}
                        expectedVersion={version}
                        allowEmpty={true}
                        onSaved={onFieldSaved} />
                </dd>
                <dt className="text-[#666]">Last name:</dt>
                <dd className="min-w-0 text-charcoal break-words">
                    <LastNameEditor
                        projectId={projectId}
                        clientIdx={clientIdx}
                        initialValue={client?.last_name}
                        expectedVersion={version}
                        allowEmpty={true}
                        onSaved={onFieldSaved} />
                </dd>
                <dt className="text-[#666]">Email:</dt>
                <dd className="min-w-0 text-charcoal break-all">
                    <ClientEmailEditor
                        projectId={projectId}
                        clientIdx={clientIdx}
                        initialValue={client?.email}
                        expectedVersion={version}
                        allowEmpty={true}
                        onSaved={onFieldSaved} />
                </dd>
                <dt className="text-[#666]">Phone:</dt>
                <dd className="min-w-0 text-charcoal break-all">
                    <PhoneEditor
                        projectId={projectId}
                        clientIdx={clientIdx}
                        initialValue={client?.phone}
                        expectedVersion={version}
                        allowEmpty={true}
                        onSaved={onFieldSaved} />
                </dd>
            </dl>
        </div>
    );
}


// `property_address` field is editable from the Project Summary — no
// other field is mutable via this component.
function AddressEditor({ projectId, initialValue, expectedVersion, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(initialValue || "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => { if (!editing) setDraft(initialValue || ""); },
        [initialValue, editing]);

    const start = () => { setDraft(initialValue || ""); setEditing(true); setError(null); };
    const cancel = () => { setDraft(initialValue || ""); setEditing(false); setError(null); };
    const save = async () => {
        setSaving(true); setError(null);
        try {
            const r = await api.patch(
                `/admin/projects/${projectId}/property_address`,
                {
                    property_address: draft,
                    expected_record_version: expectedVersion,
                    create_op_id: `addr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                },
            );
            onSaved && onSaved({
                property_address: r.data?.property_address ?? null,
                record_version: r.data?.record_version ?? (expectedVersion + 1),
            });
            setEditing(false);
        } catch (e) {
            const code = e?.response?.data?.detail?.code;
            setError(code === "record_version_mismatch"
                ? "The project changed since you loaded it. Reload and try again."
                : "Could not save address.");
        } finally { setSaving(false); }
    };

    if (!editing) {
        return (
            <div className="flex items-start gap-1.5" data-testid="summary-address-read">
                <span className="break-words">{initialValue || <span className="text-[#999]">Not set</span>}</span>
                <CopyButton value={initialValue}
                    label="Copy address"
                    testid="summary-copy-address" />
                <button
                    type="button"
                    onClick={start}
                    aria-label="Edit address"
                    title="Edit address"
                    data-testid="summary-address-edit"
                    className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border border-[#E5E2DC] text-[#666] hover:bg-[#FAFAFA]">
                    <Pencil className="w-3 h-3" />
                </button>
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-1.5" data-testid="summary-address-edit-form">
            <textarea
                data-testid="summary-address-input"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                rows={2}
                className="w-full border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] outline-none focus:border-charcoal"
                placeholder="Full property address"
            />
            {error && <div className="text-[11px] text-terracotta" data-testid="summary-address-error">{error}</div>}
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="summary-address-save"
                    className="text-[12px] px-2.5 py-1 rounded-md bg-charcoal text-white hover:bg-black transition disabled:opacity-60">
                    {saving ? "Saving…" : "Save"}
                </button>
                <button
                    type="button"
                    onClick={cancel}
                    disabled={saving}
                    data-testid="summary-address-cancel"
                    className="text-[12px] px-2.5 py-1 rounded-md border border-[#E5E2DC] text-charcoal hover:bg-[#FAFAFA] transition">
                    Cancel
                </button>
            </div>
        </div>
    );
}


// Two-client model — "+ Add second client" button + the single
// AlertDialog remove-confirm live inside the per-slot `ClientBlock`.
// This separate component just handles the "+ Add" affordance below
// the Client 01 block.  Hidden behind `TWO_CLIENT_UI_ENABLED` until
// the operator migrator has run on production (plan invariant 2).
function AddSecondClientButton({ ws, onAdd, busy }) {
    if (!TWO_CLIENT_UI_ENABLED) return null;
    if ((ws?.clients || []).length >= 2) return null;
    return (
        <button
            type="button"
            onClick={onAdd}
            disabled={busy}
            data-testid="add-second-client-btn"
            className="mt-3 text-[12px] px-2.5 py-1 rounded-md border border-[#E5E2DC] text-charcoal hover:bg-[#FAFAFA] transition disabled:opacity-60">
            {busy ? "Adding…" : "+ Add second client"}
        </button>
    );
}


// Right-rail: Project Summary — §13 (expanded per Final Corrections).
// Surfaces every field required by §12 F plus copy controls for
// contact/property fields and inline edit for property_address only.
// Explicitly does NOT surface legacy Status / Feasibility /
// internal_notes / Delete Enquiry (§9 clean-slate rule).
function ProjectSummaryPanel({ ws, onViewFull, onAddressSaved, onClientFieldSaved, onAddSecondClient, onRemoveClient, clientAddBusy, clientRemoveBusy, onStateChanged }) {
    const [descExpanded, setDescExpanded] = useState(false);
    // §24 — Project State controls now live on this card (moved from
    // the right rail).  Fetches the authoritative state + closure
    // outcomes and hosts the Hold/Close/Resume/Reopen dialogs.
    const [pState, setPState] = useState(null);
    const [outcomes, setOutcomes] = useState([]);
    const [stateDialog, setStateDialog] = useState(null);   // "hold"|"resume"|"close"|"reopen"
    const projectId = ws?.id;
    const refreshState = useCallback(async () => {
        if (!projectId) return;
        try {
            const r = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/projects/${projectId}/state`,
                { withCredentials: true });
            setPState(r.data);
        } catch { /* rail still functions without state */ }
    }, [projectId]);
    useEffect(() => {
        if (!projectId) return;
        refreshState();
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/closure-outcomes`,
            { withCredentials: true })
            .then(r => setOutcomes(r.data?.items || []))
            .catch(() => setOutcomes([]));
    }, [projectId, refreshState]);
    const afterTransition = useCallback(async () => {
        await refreshState();
        if (typeof onStateChanged === "function") {
            try { await onStateChanged(); } catch {}
        }
    }, [refreshState, onStateChanged]);
    if (!ws) return null;
    const budget = BUDGET_LABEL[ws.construction_budget] || ws.construction_budget || "—";
    const timing = TIMING_LABEL[ws.construction_timing] || ws.construction_timing || "—";
    const situ = PROPERTY_STATUS_LABEL[ws.property_status] || ws.property_status || "—";
    const types = (ws.project_types || []).map(t => PROJECT_TYPE_LABEL[t] || t).join(", ") || "—";
    const description = ws.project_description || "";
    // R2 UI follow-up — description uses a 3-line clamp (with a
    // Read more toggle) instead of a mid-word 220-char slice.  Every
    // other value cell wraps fully.

    const InlineWithCopy = ({ value, testidValue, copyLabel, copyTestid, extraClass = "" }) => (
        <div className="flex items-start gap-1.5 min-w-0">
            <span className={`min-w-0 break-words ${extraClass}`} data-testid={testidValue}>{value || <span className="text-[#999]">—</span>}</span>
            <CopyButton value={value} label={copyLabel} testid={copyTestid} />
        </div>
    );

    return (
        <section className="bg-white border border-[#E5E2DC] rounded-lg p-4 flex flex-col gap-2 min-w-0 overflow-hidden" data-testid="rail-project-summary">
            <div className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666]">Project Details</div>

            {/* Two-client model — Client 01 (always) + Client 02 (when
                `clients.length === 2`).  Both slots are rendered by the
                same `ClientBlock` component with four per-slot editors
                (FirstName / LastName / Email / Phone), hitting the
                `/clients/{idx}/{field}` endpoints with shared OCC. */}
            <ClientBlock
                ws={ws}
                clientIdx={0}
                client={(ws.clients || [])[0]}
                onFieldSaved={onClientFieldSaved} />
            {((ws.clients || []).length === 2) && (
                <ClientBlock
                    ws={ws}
                    clientIdx={1}
                    client={ws.clients[1]}
                    onFieldSaved={onClientFieldSaved}
                    onRemove={onRemoveClient}
                    removeBusy={clientRemoveBusy} />
            )}
            <AddSecondClientButton
                ws={ws}
                onAdd={onAddSecondClient}
                busy={clientAddBusy} />

            <dl className="grid grid-cols-[130px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[13px] mt-3 pt-3 border-t border-[#E5E2DC]"
                data-testid="rail-project-summary-address-group">
                <dt className="text-[#666]">Property Address:</dt>
                <dd className="min-w-0 text-charcoal break-words" data-testid="summary-address">
                    <AddressEditor
                        projectId={ws.id}
                        initialValue={ws.property_address}
                        expectedVersion={ws.record_version || 0}
                        onSaved={onAddressSaved} />
                </dd>

                <dt className="text-[#666]">Postcode:</dt>
                <dd className="min-w-0 text-charcoal">
                    <InlineWithCopy value={ws.postcode}
                        testidValue="summary-postcode"
                        copyLabel="Copy postcode"
                        copyTestid="summary-copy-postcode" />
                </dd>

                <dt className="text-[#666]">Property Situation:</dt>
                <dd className="min-w-0 text-charcoal break-words" data-testid="summary-property-status">{situ}</dd>

                <dt className="text-[#666]">Project Type:</dt>
                <dd className="min-w-0 text-charcoal break-words" data-testid="summary-project-types">{types}</dd>

                <dt className="text-[#666]">Description:</dt>
                <dd className="min-w-0 text-charcoal break-words" data-testid="summary-project-description">
                    {description ? (
                        <>
                            <span className={descExpanded ? "block whitespace-pre-wrap" : "block whitespace-pre-wrap"}
                                style={descExpanded ? undefined : {
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                }}>
                                {description}
                            </span>
                            <button
                                type="button"
                                onClick={() => setDescExpanded(v => !v)}
                                data-testid="summary-description-toggle"
                                className="mt-1 text-[12px] underline text-[#666] hover:text-charcoal">
                                {descExpanded ? "Show less" : "Read more"}
                            </button>
                        </>
                    ) : <span className="text-[#999]">—</span>}
                </dd>

                <dt className="text-[#666]">Budget:</dt>
                <dd className="min-w-0 text-charcoal break-words" data-testid="summary-budget">{budget}</dd>

                <dt className="text-[#666]">Timing:</dt>
                <dd className="min-w-0 text-charcoal break-words" data-testid="summary-timing">{timing}</dd>

                <dt className="text-[#666]">Owner:</dt>
                <dd className="min-w-0 text-charcoal break-words" data-testid="summary-owner">{ws.owner_display_name || ws.owner_id || <span className="text-[#999]">—</span>}</dd>

                <dt className="text-[#666]">Current Workstage:</dt>
                <dd className="min-w-0 text-charcoal break-words" data-testid="summary-workstage">
                    {ws.workstage}. {(WORKSTAGES.find(w => w.num === ws.workstage) || {}).name || "—"}
                </dd>

                <dt className="text-[#666]">Project State:</dt>
                <dd className="min-w-0 text-charcoal break-words" data-testid="summary-project-state">{ws.project_state || "Active"}</dd>

                <dt className="text-[#666]">Next Action:</dt>
                <dd className="min-w-0 text-charcoal break-words" data-testid="summary-next-action">
                    {renderNextAction(ws.next_action) || <span className="text-[#999]">—</span>}
                </dd>
            </dl>
            <button
                type="button"
                onClick={onViewFull}
                data-testid="rail-project-summary-view-full"
                className="mt-2 w-full text-[13px] py-2 border border-[#E5E2DC] rounded-md hover:bg-[#FAFAFA] transition">
                View full details
            </button>
            {/* §24 — Project State contextual buttons moved onto the
                Project Details card.  Rendered from `pState.valid_actions`
                so they only show when the transition is legal;
                destructive colours preserved from the previous
                right-rail treatment. */}
            {pState && (pState.valid_actions || []).map((a) => {
                const isReopen = a === "reopen_project";
                const isClose = a === "close_project";
                const isHold = a === "place_on_hold";
                const isResume = a === "resume_project";
                const tone = isReopen
                    ? "bg-emerald-700 hover:bg-emerald-800"
                    : isClose
                        ? "bg-red-700 hover:bg-red-800"
                        : isHold
                            ? "bg-amber-700 hover:bg-amber-800"
                            : isResume
                                ? "bg-[#A8543F] hover:bg-[#8B4535]"
                                : "bg-[#A8543F] hover:bg-[#8B4535]";
                const label = ({
                    place_on_hold: "Place on Hold",
                    resume_project: "Resume Project",
                    close_project: "Close Project",
                    reopen_project: "Reopen Project",
                })[a] || a;
                return (
                    <button key={a} type="button"
                            onClick={() => setStateDialog(a === "place_on_hold" ? "hold"
                                : a === "resume_project" ? "resume"
                                : a === "close_project" ? "close"
                                : "reopen")}
                            className={`w-full ${tone} text-white text-[13px] py-2 rounded-md text-center`}
                            data-testid={`project-details-${a}`}>
                        {label}
                    </button>);
            })}
            {stateDialog === "hold" && pState && (
                <HoldDialog projectId={projectId} state={pState}
                            onClose={() => setStateDialog(null)}
                            onDone={() => { setStateDialog(null); afterTransition(); }} />)}
            {stateDialog === "resume" && pState && (
                <ResumeDialog projectId={projectId} state={pState}
                              onClose={() => setStateDialog(null)}
                              onDone={() => { setStateDialog(null); afterTransition(); }} />)}
            {stateDialog === "close" && pState && (
                <CloseDialog projectId={projectId} state={pState}
                             outcomes={outcomes}
                             onClose={() => setStateDialog(null)}
                             onDone={() => { setStateDialog(null); afterTransition(); }} />)}
            {stateDialog === "reopen" && pState && (
                <ReopenDialog projectId={projectId} state={pState}
                              onClose={() => setStateDialog(null)}
                              onDone={() => { setStateDialog(null); afterTransition(); }} />)}
        </section>
    );
}

// In-workspace "View Full Details" drawer — §8. Read-only reference /
// attribution content only. NEVER exposes legacy Status / Feasibility /
// internal_notes / Delete Enquiry (§9). Absent values are shown as an
// em-dash — never invented.
function ViewFullDetailsDrawer({ open, onClose, ws }) {
    if (!open || !ws) return null;
    const a = ws.attribution || {};
    const meta = a.meta || {};
    const utm = a.utm || {};
    const src = a.source_detail || {};
    const Row = ({ k, v, testid }) => (
        <>
            <dt className="text-[#666] text-[12px]">{k}</dt>
            <dd className="text-charcoal text-[13px] break-words" data-testid={testid}>
                {v == null || v === "" ? <span className="text-[#999]">—</span> : String(v)}
            </dd>
        </>
    );
    return (
        <div
            className="fixed inset-0 z-40 flex justify-end"
            role="dialog"
            aria-modal="true"
            aria-label="Full project details"
            data-testid="workspace-full-details-drawer">
            <div className="flex-1 bg-black/30" onClick={onClose} data-testid="workspace-full-details-scrim" />
            <div className="w-[420px] max-w-[95vw] h-full bg-white shadow-lg overflow-y-auto flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E2DC]">
                    <div className="font-manrope font-semibold text-charcoal text-[15px]">
                        Full project details
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="workspace-full-details-close"
                        className="p-1 rounded hover:bg-[#FAFAFA]">
                        <XIcon className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-4 flex flex-col gap-4">
                    <section>
                        <h3 className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666] mb-2">Project</h3>
                        <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1">
                            <Row k="Project ID" v={ws.project_ref} testid="drawer-project-id" />
                            <Row k="Client" v={ws.client_name} testid="drawer-client-name" />
                        </dl>
                    </section>
                    <section>
                        <h3 className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666] mb-2">Source</h3>
                        <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1">
                            <Row k="Source" v={a.source} testid="drawer-source" />
                            <Row k="Created" v={a.created_at} testid="drawer-created-at" />
                            <Row k="Updated" v={a.updated_at} testid="drawer-updated-at" />
                            {/* D5 correction — landing / referrer only when
                                Website intake actually captures them. */}
                            {a.source === "Website" && (
                                <>
                                    <Row k="Landing URL" v={a.landing_url} testid="drawer-landing-url" />
                                    <Row k="Referrer" v={a.referrer_url} testid="drawer-referrer-url" />
                                </>
                            )}
                        </dl>
                    </section>
                    {a.source === "Meta" && (
                        <section>
                            <h3 className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666] mb-2">Meta</h3>
                            <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1">
                                <Row k="Lead ID" v={meta.lead_id} testid="drawer-meta-lead-id" />
                                <Row k="Form ID" v={meta.form_id} testid="drawer-meta-form-id" />
                                <Row k="Page ID" v={meta.page_id} testid="drawer-meta-page-id" />
                                <Row k="Campaign" v={meta.campaign_name} testid="drawer-meta-campaign-name" />
                                <Row k="Campaign ID" v={meta.campaign_id} testid="drawer-meta-campaign-id" />
                                <Row k="Ad set" v={meta.adset_name} testid="drawer-meta-adset-name" />
                                <Row k="Ad set ID" v={meta.adset_id} testid="drawer-meta-adset-id" />
                                <Row k="Ad" v={meta.ad_name} testid="drawer-meta-ad-name" />
                                <Row k="Ad ID" v={meta.ad_id} testid="drawer-meta-ad-id" />
                                {/* D5 correction — Meta ingest does not
                                    capture Creative or Placement. Rows
                                    removed to avoid meaningless em-dashes. */}
                            </dl>
                        </section>
                    )}
                    {a.source === "Website" && a.utm && (
                        <section>
                            <h3 className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666] mb-2">UTM</h3>
                            <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1">
                                <Row k="utm_source" v={utm.source} testid="drawer-utm-source" />
                                <Row k="utm_medium" v={utm.medium} testid="drawer-utm-medium" />
                                <Row k="utm_campaign" v={utm.campaign} testid="drawer-utm-campaign" />
                                <Row k="utm_content" v={utm.content} testid="drawer-utm-content" />
                                <Row k="utm_term" v={utm.term} testid="drawer-utm-term" />
                            </dl>
                        </section>
                    )}
                    {Object.keys(src).length > 0 && (
                        <section>
                            <h3 className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666] mb-2">
                                Raw source_detail
                            </h3>
                            <pre className="text-[11px] bg-[#FAFAFA] border border-[#E5E2DC] rounded-md p-2 whitespace-pre-wrap break-words" data-testid="drawer-raw-source-detail">
                                {JSON.stringify(src, null, 2)}
                            </pre>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}

// Reusable compact admin dialog shell for right-rail quick actions
// (Log Call / Internal Note / Follow-up). Deliberately small — Escape
// closes when not submitting; backdrop click closes when not
// submitting; Save is disabled while a request is in flight; focus is
// returned to the triggering button on close where practical.
function QuickActionDialog({ open, title, onClose, onSave, submitting, saveLabel, saveTestId, children }) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, submitting, onClose]);
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
            data-testid="quick-action-backdrop">
            <div className="bg-white rounded-lg shadow-lg w-[440px] max-w-[92vw] flex flex-col gap-3 p-4 border border-[#E5E2DC]"
                role="dialog" aria-modal="true" aria-label={title}>
                <div className="flex items-center justify-between">
                    <div className="font-manrope font-semibold text-[15px] text-charcoal">{title}</div>
                    <button type="button" onClick={onClose} disabled={submitting}
                        aria-label="Close" data-testid="quick-action-close"
                        className="text-[#666] hover:text-charcoal disabled:opacity-40">
                        <XIcon className="w-4 h-4" />
                    </button>
                </div>
                <div className="flex flex-col gap-3">{children}</div>
                <div className="flex justify-end gap-2 pt-1">
                    <button type="button" onClick={onClose} disabled={submitting}
                        data-testid="quick-action-cancel"
                        className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal hover:bg-[#FAFAFA] disabled:opacity-40">
                        Cancel
                    </button>
                    <button type="button" onClick={onSave} disabled={submitting}
                        data-testid={saveTestId || "quick-action-save"}
                        className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md hover:bg-black disabled:opacity-40">
                        {submitting ? "Saving…" : saveLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

const _nowLocalInputValue = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function LogCallDialog({ open, onClose, projectId, onSaved }) {
    const [at, setAt] = useState(_nowLocalInputValue());
    const [body, setBody] = useState("");
    const [err, setErr] = useState("");
    const [submitting, setSubmitting] = useState(false);
    useEffect(() => {
        if (open) { setAt(_nowLocalInputValue()); setBody(""); setErr(""); setSubmitting(false); }
    }, [open]);
    const save = async () => {
        setErr("");
        const t = body.trim();
        if (!t) { setErr("Call summary is required."); return; }
        setSubmitting(true);
        try {
            const isoAt = at ? new Date(at).toISOString() : null;
            await api.post(`/admin/projects/${projectId}/activity/append`,
                { kind: "log_call", body: t, at: isoAt });
            toast.success("Call logged");
            onSaved?.();
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Failed to save call.");
        } finally { setSubmitting(false); }
    };
    return (
        <QuickActionDialog open={open} onClose={onClose} onSave={save}
            submitting={submitting} title="Log Call"
            saveLabel="Save Call" saveTestId="log-call-save">
            <label className="flex flex-col gap-1">
                <span className="text-[12px] font-manrope font-semibold uppercase tracking-wide text-[#666]">Call date &amp; time</span>
                <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)}
                    data-testid="log-call-at"
                    className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
            </label>
            <label className="flex flex-col gap-1">
                <span className="text-[12px] font-manrope font-semibold uppercase tracking-wide text-[#666]">Call summary</span>
                <textarea value={body} onChange={(e) => setBody(e.target.value)}
                    rows={4} placeholder="Who did you speak to, and what was agreed?"
                    data-testid="log-call-body"
                    className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
            </label>
            {err && <div data-testid="log-call-error" className="text-[12px] text-terracotta">{err}</div>}
        </QuickActionDialog>
    );
}


function InternalNoteDialog({ open, onClose, projectId, onSaved }) {
    const [body, setBody] = useState("");
    const [err, setErr] = useState("");
    const [submitting, setSubmitting] = useState(false);
    useEffect(() => {
        if (open) { setBody(""); setErr(""); setSubmitting(false); }
    }, [open]);
    const save = async () => {
        setErr("");
        const t = body.trim();
        if (!t) { setErr("Note is required."); return; }
        setSubmitting(true);
        try {
            await api.post(`/admin/projects/${projectId}/activity/append`,
                { kind: "internal_note", body: t });
            toast.success("Note added");
            onSaved?.();
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Failed to add note.");
        } finally { setSubmitting(false); }
    };
    return (
        <QuickActionDialog open={open} onClose={onClose} onSave={save}
            submitting={submitting} title="Add Internal Note"
            saveLabel="Save Note" saveTestId="internal-note-save">
            <label className="flex flex-col gap-1">
                <span className="text-[12px] font-manrope font-semibold uppercase tracking-wide text-[#666]">Internal note</span>
                <textarea value={body} onChange={(e) => setBody(e.target.value)}
                    rows={5} placeholder="Internal-only note. Historical notes cannot be edited or deleted."
                    data-testid="internal-note-body"
                    className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
            </label>
            {err && <div data-testid="internal-note-error" className="text-[12px] text-terracotta">{err}</div>}
        </QuickActionDialog>
    );
}

function FollowUpDialog({ open, onClose, projectId, onSaved }) {
    const [due, setDue] = useState(_nowLocalInputValue());
    const [body, setBody] = useState("");
    const [err, setErr] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [opId] = useState(() => `fu-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    useEffect(() => {
        if (open) { setDue(_nowLocalInputValue()); setBody(""); setErr(""); setSubmitting(false); }
    }, [open]);
    const save = async () => {
        setErr("");
        const t = body.trim();
        if (!t) { setErr("Follow-up note is required."); return; }
        if (!due) { setErr("Follow-up date/time is required."); return; }
        setSubmitting(true);
        try {
            const isoDue = new Date(due).toISOString();
            // Authoritative A3 Follow-up create — persists a first-class
            // record in `project_follow_ups`; the audit `follow_up_created`
            // Project Activity event is emitted by the service itself.
            await api.post(`/admin/projects/${projectId}/follow-ups`,
                { due_at: isoDue, purpose_note: t,
                  follow_up_type: "admin", create_op_id: opId });
            toast.success("Follow-up saved");
            onSaved?.();
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Failed to save follow-up.");
        } finally { setSubmitting(false); }
    };
    return (
        <QuickActionDialog open={open} onClose={onClose} onSave={save}
            submitting={submitting} title="Set Follow-up"
            saveLabel="Save Follow-up" saveTestId="follow-up-save">
            <label className="flex flex-col gap-1">
                <span className="text-[12px] font-manrope font-semibold uppercase tracking-wide text-[#666]">Follow-up date &amp; time</span>
                <input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)}
                    data-testid="follow-up-due"
                    className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
            </label>
            <label className="flex flex-col gap-1">
                <span className="text-[12px] font-manrope font-semibold uppercase tracking-wide text-[#666]">Follow-up / next action</span>
                <textarea value={body} onChange={(e) => setBody(e.target.value)}
                    rows={4} placeholder="What is the next action?"
                    data-testid="follow-up-body"
                    className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
            </label>
            {err && <div data-testid="follow-up-error" className="text-[12px] text-terracotta">{err}</div>}
        </QuickActionDialog>
    );
}

// Send Communication dialog (B3.5) — template picker → preview → send.
// Preview is the confirmation step; no second modal.
function SendCommunicationDialog({ open, onClose, projectId }) {
    const [templates, setTemplates] = useState([]);
    const [selected, setSelected] = useState("");
    const [merge, setMerge] = useState({});
    const [preview, setPreview] = useState(null);
    const [previewErr, setPreviewErr] = useState("");
    const [err, setErr] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [opId] = useState(() => `sc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    useEffect(() => {
        if (!open) return;
        setTemplates([]); setSelected(""); setMerge({});
        setPreview(null); setPreviewErr(""); setErr(""); setSubmitting(false);
        api.get("/admin/communication-templates").then(r => {
            const items = r.data?.items || [];
            setTemplates(items);
            if (items.length) setSelected(items[0].template_code);
        }).catch(() => setErr("Failed to load templates."));
    }, [open]);
    const tv = templates.find(t => t.template_code === selected);
    const requestPreview = async () => {
        setPreviewErr(""); setPreview(null);
        if (!tv) return;
        try {
            const r = await api.post(
                `/admin/projects/${projectId}/communications/preview`,
                { template_code: tv.template_code,
                  merge_values: merge });
            setPreview(r.data);
        } catch (e) {
            setPreviewErr(e?.response?.data?.detail || "Preview failed.");
        }
    };
    const send = async () => {
        setErr("");
        if (!preview) { setErr("Preview the message before sending."); return; }
        setSubmitting(true);
        try {
            await api.post(
                `/admin/projects/${projectId}/communications/send`,
                { template_code: tv.template_code,
                  merge_values: merge,
                  create_op_id: opId });
            toast.success("Communication sent");
            onClose();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            let msg = "Send failed.";
            if (typeof detail === "string") msg = detail;
            else if (detail?.error_class) msg = `Send failed (${detail.error_class}).`;
            setErr(msg);
        } finally { setSubmitting(false); }
    };
    return (
        <QuickActionDialog open={open} onClose={onClose} onSave={send}
            submitting={submitting} title="Send Communication"
            saveLabel="Send" saveTestId="send-comm-send">
            <label className="flex flex-col gap-1">
                <span className="text-[12px] font-manrope font-semibold uppercase tracking-wide text-[#666]">Template</span>
                <select value={selected} onChange={e => { setSelected(e.target.value); setPreview(null); }}
                    data-testid="send-comm-template"
                    className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]">
                    {templates.map(t => (
                        <option key={t.template_code} value={t.template_code}>
                            {t.display_name} · {t.communication_class} · {t.delivery_control_mode}
                        </option>
                    ))}
                </select>
            </label>
            {tv && (tv.supported_merge_fields || []).map(f => (
                <label key={f.key} className="flex flex-col gap-1">
                    <span className="text-[12px] font-manrope font-semibold uppercase tracking-wide text-[#666]">
                        {f.key}{f.required ? " *" : ""}
                    </span>
                    <input value={merge[f.key] || ""}
                        onChange={e => { setMerge(m => ({ ...m, [f.key]: e.target.value })); setPreview(null); }}
                        data-testid={`send-comm-merge-${f.key}`}
                        className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                </label>
            ))}
            <button type="button" onClick={requestPreview}
                data-testid="send-comm-preview"
                className="self-start text-[12px] px-3 py-1.5 border border-[#E5E2DC] rounded-md hover:bg-[#FAFAFA]">
                Preview
            </button>
            {previewErr && <div data-testid="send-comm-preview-error" className="text-[12px] text-terracotta">{previewErr}</div>}
            {preview && (
                <div data-testid="send-comm-preview-panel" className="border border-[#E5E2DC] rounded-md p-3 bg-[#FAFAFA] flex flex-col gap-2">
                    <div className="text-[11px] font-manrope font-semibold uppercase tracking-wide text-[#666]">
                        WS{preview.workstage} · {preview.communication_class} · {preview.delivery_control_mode}
                    </div>
                    <div className="text-[11px] text-[#666]">
                        To: <span data-testid="send-comm-preview-to">{preview.recipient?.address}</span>
                    </div>
                    <div className="text-[11px] text-[#666]">
                        From: {preview.sender?.display}
                    </div>
                    <div className="text-[13px] font-semibold text-charcoal" data-testid="send-comm-preview-subject">
                        {preview.rendered_subject}
                    </div>
                    <pre data-testid="send-comm-preview-body" className="whitespace-pre-wrap text-[12.5px] text-charcoal font-sans m-0">{preview.rendered_body}</pre>
                </div>
            )}
            {err && <div data-testid="send-comm-error" className="text-[12px] text-terracotta">{err}</div>}
        </QuickActionDialog>
    );
}

// Right-rail: Actions — §2, §13 (V1). Canonical single Actions area
// after the V1 correction gate.  Merged from the prior separate
// ProjectStateRail + ActionsPanel components: renders BOTH the
// functional B14 Project State controls (contextual per
// `valid_actions` returned by `/admin/projects/{id}/state`) AND the
// routine operational actions in one section.  All controls execute
// real handlers — nothing is a placeholder any more.  Owner-of-record
// implementation markers (B14 / B4 / B3 / B3-b / B3-c / B3.5) are
// intentionally preserved in this file as internal metadata used for
// `data-testid` generation and routing, but are NEVER rendered as
// user-facing text (§4).
// §25 / §25a — DRY pending/overdue label (exported for Jest coverage).
//   Spec (§25 Items 2 & 3):
//   - pending == 0, overdue == 0 → "Pending: 0" (row still visible).
//   - pending  > 0, overdue == 0 → "Pending: N".
//   - pending  > 0, overdue  > 0 → "Pending: N (Overdue: M)".
//   Only skip when the caller passes no count at all
//   (`pending === undefined || null` — the fetch has not resolved yet).
export function PendingCount({ pending, overdue, testid }) {
    if (pending === undefined || pending === null) return null;
    const p = Number(pending) || 0;
    const o = Number(overdue) || 0;
    return (
        <span className="text-xs text-slate-500 text-right self-center whitespace-nowrap"
               data-testid={testid}>
            {`Pending: ${p}${o > 0 ? ` (Overdue: ${o})` : ""}`}
        </span>
    );
}


function ActionsPanel({ projectId, currentWorkstage, actionCounts, onStateChanged }) {
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(null); // 'follow_up' | null (remaining after §24)
    const triggerRefs = useRef({});
    const closeDialog = () => {
        const returnTo = triggerRefs.current[openDialog];
        setOpenDialog(null);
        setTimeout(() => { try { returnTo?.focus(); } catch {} }, 0);
    };
    // §24 — Right-rail redesign Pass 1:
    //   - Project State group removed (moved to Project Details card).
    //   - Communication group removed entirely (Log Call / View Call
    //     Log / Send Communication / View Communications all folded
    //     into the bottom Communications and Notes container).
    //   - Add Internal Note removed from Project Management (moved to
    //     the Communications and Notes container).
    //   - `View WS1` remains deleted (§23).
    // §25 — 40:60 two-column grid; right column carries pending /
    //        overdue counts for Client Actions, RFI, Follow-ups, Tasks.
    const openDlg = (key) => (e) => {
        triggerRefs.current[key] = e.currentTarget;
        setOpenDialog(key);
    };
    const counts = actionCounts || {
        client_actions: {}, rfis: {}, follow_ups: {}, tasks: {},
    };
    const clientActionRows = [
        { label: "Client Actions", onClick: () => navigate(`/admin/projects/${projectId}/client-actions`),
          count: counts.client_actions },
        { label: "Request Information", onClick: () => navigate(`/admin/projects/${projectId}/rfi`),
          count: counts.rfis },
    ];
    const projectManagementRows = [
        { label: "Set Follow Up", onClick: openDlg("follow_up"),
          count: counts.follow_ups },
        { label: "Programme", onClick: () => navigate(`/admin/projects/${projectId}/programme`) },
        { label: "Feasibility Study", onClick: () => navigate(`/admin/projects/${projectId}/feasibility-study`) },
        { label: "Files", onClick: () => navigate(`/admin/projects/${projectId}/files`) },
        { label: "Tasks", onClick: () => navigate(`/admin/projects/${projectId}/tasks`),
          count: counts.tasks },
        { label: "Activity Log", onClick: () => navigate(`/admin/projects/${projectId}/activity`) },
    ];
    // Header styling — pinned to the previous "Project State" header
    // treatment (matches every group header in this rail).
    const groupHeaderCls =
        "text-[10px] uppercase tracking-wide font-manrope font-semibold text-[#999]";
    const GroupBtn = ({ label, onClick, variant }) => {
        const testid = `rail-action-${label.toLowerCase().replace(/\s+/g, "-")}`;
        const tone = {
            slate: "bg-slate-100 hover:bg-slate-200 text-slate-900 border-slate-300",
            stone: "bg-stone-100 hover:bg-stone-200 text-stone-900 border-stone-300",
            zinc:  "bg-zinc-100 hover:bg-zinc-200 text-zinc-900 border-zinc-300",
        }[variant] || "bg-white hover:bg-[#FAFAFA] text-charcoal border-[#E5E2DC]";
        return (
            <button
                type="button"
                onClick={onClick}
                aria-label={label}
                title={label}
                data-testid={testid}
                className={`w-full text-[13px] py-2 px-3 border rounded-md text-left transition cursor-pointer ${tone}`}>
                <span>{label}</span>
            </button>
        );
    };
    // §25 / §25a — DRY pending/overdue label (module-level export).
    //   Now exported at module scope; inline stub kept as a
    //   reference for parity with the exported function.
    /* eslint-disable-next-line no-unused-vars */
    const _PendingCountAlias = PendingCount;
    const CountRow = ({ label, onClick, variant, count }) => {
        const slug = label.toLowerCase().replace(/\s+/g, "-");
        // Render the PendingCount whenever the caller passed a count
        // object.  When `pending` is undefined (before the fetch
        // resolves) the component itself returns null — no flicker.
        return (
            <div className="grid grid-cols-[40%_60%] items-center gap-2">
                <div><GroupBtn label={label} onClick={onClick} variant={variant} /></div>
                <div className="pl-2">
                    {count !== undefined && count !== null ? (
                        <PendingCount pending={count.pending}
                                       overdue={count.overdue}
                                       testid={`rail-count-${slug}`} />
                    ) : null}
                </div>
            </div>
        );
    };
    return (
        <section className="bg-white border border-[#E5E2DC] rounded-lg p-4 flex flex-col gap-3 min-w-0 overflow-hidden" data-testid="rail-actions">
            <div className="flex items-center justify-between">
                <div className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666]">Actions</div>
            </div>

            {/* Group · Client Actions (§23 slate; §25 40:60 with counts). */}
            <div className="flex flex-col gap-2" data-testid="rail-group-client-actions">
                <div className={groupHeaderCls}>Client Actions</div>
                {clientActionRows.map(a => (
                    <CountRow key={a.label} label={a.label} onClick={a.onClick}
                               variant="slate" count={a.count} />
                ))}
            </div>

            {/* Group · Project Management (§23 stone; §24 — Add
                Internal Note moved to Communications and Notes; §25
                40:60 with counts where relevant, empty right cell
                otherwise). */}
            <div className="pt-3 border-t border-[#E5E2DC] flex flex-col gap-2" data-testid="rail-group-project-management">
                <div className={groupHeaderCls}>Project Management</div>
                {projectManagementRows.map(a => (
                    <CountRow key={a.label} label={a.label} onClick={a.onClick}
                               variant="stone" count={a.count} />
                ))}
            </div>

            <FollowUpDialog open={openDialog === "follow_up"} onClose={closeDialog} projectId={projectId} />
        </section>
    );
}

// Right-rail: Communications — §15. Passthrough view; NO sending, NO
// fake sample rows. Uses authoritative A7 `project_communications`
// records when present. Empty state is deliberate and honest.
function CommunicationsPanel({ items, projectId, refreshWorkspace }) {
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(null); // 'internal_note' | 'log_call' | 'send_comm' | null
    const triggerRefs = useRef({});
    const openDlg = (key) => (e) => {
        triggerRefs.current[key] = e.currentTarget;
        setOpenDialog(key);
    };
    const closeDialog = () => {
        const returnTo = triggerRefs.current[openDialog];
        setOpenDialog(null);
        setTimeout(() => { try { returnTo?.focus(); } catch {} }, 0);
    };
    // §24 — merged rows (emails + calls + notes).  Fetched from the
    // unified endpoint so a single request drives both this at-a-
    // glance summary and the full page.  `items` (legacy email-only
    // prop) is kept as a synchronous fallback while the merged fetch
    // is in flight.
    const [merged, setMerged] = useState(null);
    const [mergedErr, setMergedErr] = useState("");
    const loadMerged = useCallback(async () => {
        setMergedErr("");
        try {
            const r = await api.get(
                `/admin/projects/${projectId}/comms-and-notes?limit=3`);
            setMerged(r.data?.items || []);
        } catch (e) { setMergedErr(e?.response?.data?.detail || e.message || ""); }
    }, [projectId]);
    useEffect(() => { if (projectId) loadMerged(); }, [projectId, loadMerged]);
    const onSaved = async () => {
        await loadMerged();
        if (typeof refreshWorkspace === "function") {
            try { await refreshWorkspace(); } catch {}
        }
    };
    const zinc = "bg-zinc-100 hover:bg-zinc-200 text-zinc-900 border-zinc-300";
    const rows = merged;
    const typeBadge = {
        email: "✉ Email",
        call:  "☏ Call",
        note:  "🗒 Note",
    };
    const rowsToRender = rows === null
        ? (items || []).slice(0, 3).map(c => ({
            type: "email", ts: c.sent_at,
            excerpt: c.subject || "—",
            by: c.direction || null,
            ref_id: c.id,
            meta: {},
        }))
        : rows.slice(0, 3);
    return (
        <section className="bg-white border border-[#E5E2DC] rounded-lg p-4 flex flex-col gap-3 min-w-0 overflow-hidden" data-testid="rail-communications">
            <div className="flex items-center justify-between">
                <div className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666]">Communications and Notes</div>
                {rows !== null && (
                    <span className="text-[11px] text-[#999]" data-testid="rail-communications-count">{rows.length}</span>
                )}
            </div>
            {/* §24 — action buttons live at the top of the container. */}
            <div className="flex flex-col gap-2" data-testid="rail-comms-notes-actions">
                <button type="button" onClick={openDlg("internal_note")}
                        aria-label="Add Internal Note" title="Add Internal Note"
                        data-testid="rail-action-add-internal-note"
                        className={`w-full text-[13px] py-2 px-3 border rounded-md text-left transition cursor-pointer ${zinc}`}>
                    Add Internal Note
                </button>
                <button type="button" onClick={openDlg("log_call")}
                        aria-label="Log Call" title="Log Call"
                        data-testid="rail-action-log-call"
                        className={`w-full text-[13px] py-2 px-3 border rounded-md text-left transition cursor-pointer ${zinc}`}>
                    Log Call
                </button>
                <button type="button" onClick={openDlg("send_comm")}
                        aria-label="Send Communication" title="Send Communication"
                        data-testid="rail-action-send-communication"
                        className={`w-full text-[13px] py-2 px-3 border rounded-md text-left transition cursor-pointer ${zinc}`}>
                    Send Communication
                </button>
            </div>
            {mergedErr && (
                <div className="text-[12px] text-terracotta" data-testid="rail-comms-notes-err">{mergedErr}</div>
            )}
            {rows !== null && rows.length === 0 ? (
                <div className="text-[12.5px] text-[#999]" data-testid="rail-communications-empty">
                    No communications or notes yet.
                </div>
            ) : rowsToRender.length === 0 ? (
                <div className="text-[12.5px] text-[#999]" data-testid="rail-communications-empty">
                    No communications or notes yet.
                </div>
            ) : (
                <ul className="flex flex-col gap-2">
                    {rowsToRender.map((r, idx) => (
                        <li key={r.ref_id || idx}
                            className="border border-[#E5E2DC] rounded-md p-2 min-w-0"
                            data-testid={`rail-comms-notes-item-${r.ref_id || idx}`}>
                            <div className="flex items-center gap-1.5 text-[12px] font-manrope font-semibold uppercase tracking-wide text-[#666]">
                                <span data-testid={`rail-comms-notes-item-${r.ref_id || idx}-type`}>
                                    {typeBadge[r.type] || r.type}
                                </span>
                                {r.meta && r.meta.source === "external_send" && (
                                    <span className="inline-flex rounded-full bg-[#F0EBE1] px-1.5 py-0.5 text-[9.5px] uppercase tracking-wide text-[#5A4B2A] font-semibold"
                                          data-testid={`rail-comms-notes-item-${r.ref_id || idx}-ext-badge`}>
                                        Sent externally
                                    </span>
                                )}
                                <span className="ml-auto text-[11px] text-[#999] font-normal normal-case tracking-normal"
                                       data-testid={`rail-comms-notes-item-${r.ref_id || idx}-ts`}>
                                    {fmtDate(r.ts)}
                                </span>
                            </div>
                            <div className="mt-1 text-[13px] text-charcoal break-words line-clamp-1"
                                 data-testid={`rail-comms-notes-item-${r.ref_id || idx}-excerpt`}>
                                {r.excerpt || <span className="text-[#999]">—</span>}
                            </div>
                            {r.by && (
                                <div className="text-[11px] text-[#999]"
                                      data-testid={`rail-comms-notes-item-${r.ref_id || idx}-by`}>
                                    {r.type === "note" ? (r.by_display_name || r.by) : r.by}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            <button
                type="button"
                onClick={() => navigate(`/admin/projects/${projectId}/communications`)}
                data-testid="rail-communications-view-all"
                className="text-[12.5px] text-[#666] hover:text-charcoal underline text-left cursor-pointer">
                View communications and notes
            </button>

            <InternalNoteDialog open={openDialog === "internal_note"} onClose={closeDialog} projectId={projectId} onSaved={onSaved} />
            <LogCallDialog open={openDialog === "log_call"} onClose={closeDialog} projectId={projectId} onSaved={onSaved} />
            <SendCommunicationDialog open={openDialog === "send_comm"} onClose={closeDialog} projectId={projectId} />
        </section>
    );
}

// Right-rail: Attention Needed — §16, §17. Viewing MUST NOT resolve.
// When none exist: visually quiet. When present: prominent but no
// generic resolution path (B3 owns operational Attention tooling).
function AttentionNeededPanel({ items, projectId }) {
    const navigate = useNavigate();
    const has = items && items.length > 0;
    return (
        <section
            data-testid="rail-attention-needed"
            data-has-items={has ? "true" : "false"}
            className={`border rounded-lg p-4 flex flex-col gap-2 min-w-0 overflow-hidden ${has ? "bg-[#FDF4F2] border-terracotta/30" : "bg-white border-[#E5E2DC]"}`}>
            <div className="flex items-center gap-1.5">
                <span className={`font-manrope font-semibold text-[11px] uppercase tracking-wide ${has ? "text-terracotta" : "text-[#666]"}`}>
                    Attention Needed
                </span>
                {has && <AlertTriangle className="w-3.5 h-3.5 text-terracotta" />}
                <span className="ml-auto text-[11px] text-[#666]">{items?.length || 0}</span>
            </div>
            {has ? (
                <ul className="flex flex-col gap-2 mt-1">
                    {items.map(a => (
                        <li key={a.id} className="text-[13px] text-charcoal min-w-0 break-words" data-testid={`rail-attention-item-${a.id}`}>
                            <div className="font-medium break-words">{a.title}</div>
                            <div className="text-[11px] text-[#666]">{fmtDate(a.created_at)}</div>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="text-[12.5px] text-[#999]" data-testid="rail-attention-empty">
                    No unresolved attention.
                </div>
            )}
            <button
                type="button"
                onClick={() => navigate(`/admin/projects/${projectId}/attention`)}
                data-testid="rail-attention-view-all"
                className="text-[12.5px] text-[#666] hover:text-charcoal underline text-left cursor-pointer">
                View all attention
            </button>
        </section>
    );
}

export default function ProjectWorkspace() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ws, setWs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedWs, setExpandedWs] = useState(() => new Set());
    const [showFullDetails, setShowFullDetails] = useState(false);
    // §25 — pending/overdue counts for right-rail 40:60 grid.
    const [actionCounts, setActionCounts] = useState(null);
    const projectLabel = ws?.client_name || "Project";
    usePageMeta({ title: `${projectLabel} · Project | Studio G4 Admin` });

    const refreshActionCounts = useCallback(async () => {
        try {
            const r = await api.get(`/admin/projects/${id}/action-counts`);
            setActionCounts(r.data || null);
        } catch { /* rail renders without counts on failure */ }
    }, [id]);

    const refresh = useCallback(() => {
        return api.get(`/admin/projects/${id}/workspace`)
            .then(r => {
                setWs(r.data);
                setExpandedWs(prev => {
                    // Preserve any expansions the admin has made; only
                    // seed the current WS if the accordion state is
                    // still empty (e.g. very first load).
                    if (prev && prev.size > 0) return prev;
                    return new Set([r.data?.workstage || 1]);
                });
                // §25 — refresh counts alongside workspace load so
                //        both are consistent from the user's viewpoint.
                refreshActionCounts();
                return r.data;
            })
            .catch(err => {
                if (err?.response?.status === 404) {
                    setError("Project not found");
                } else {
                    setError("Could not load project workspace");
                }
            });
    }, [id, refreshActionCounts]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true); setError(null);
        api.get(`/admin/projects/${id}/workspace`)
            .then(r => {
                if (cancelled) return;
                setWs(r.data);
                setExpandedWs(new Set([r.data?.workstage || 1]));
                // §25a — Bug 1 (recurrence) fix: also load the right-rail
                //         pending/overdue counts on initial mount.  Prior
                //         to this, refreshActionCounts only fired via
                //         onStateChanged, so first-load left actionCounts
                //         === null → count.pending === undefined →
                //         <PendingCount/> returned null → the right cell
                //         of the 40:60 grid rendered empty.
                refreshActionCounts();
            })
            .catch(err => {
                if (cancelled) return;
                if (err?.response?.status === 404) {
                    setError("Project not found");
                } else {
                    setError("Could not load project workspace");
                }
            })
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [id, refreshActionCounts]);

    // §2 — authoritative Workstage state map (workstage → visual
    // status). Comes from `ws.workstage_states`; NEVER derived from
    // `stage_number < currentWs`. Falls back to "future" when the
    // authoritative row is absent for a given WS (e.g. before the
    // workspace payload loads).
    const authoritativeStates = useMemo(() => {
        const map = {};
        for (const s of (ws?.workstage_states || [])) {
            map[s.workstage] = s.state;
        }
        return map;
    }, [ws]);

    const stageUiStatus = useCallback((num) => {
        const st = authoritativeStates[num];
        const projState = ws?.project_state || "Active";
        // §1 Correction — same shared derivation as WorkstageRibbon:
        // never present a WS as visually "active" while Project State
        // is On Hold or Closed.  Record state remains authoritative;
        // only the visual role changes.
        if (st === "Active" && projState === "On Hold") return "on-hold";
        if (st === "Active" && projState === "Closed") return "ended-by-closure";
        if (st === "Active") return "active";
        if (st === "Completed") return "completed";
        if (st === "Skipped / Not Entered") return "skipped";
        if (st === "Ended by Project Closure") return "ended-by-closure";
        return "future"; // includes "Not Started"
    }, [authoritativeStates, ws]);

    // D5 Slice A.6 — shared frontend `mode` scaffold.
    // `mode: "future" | "active" | "completed"` is the contract the
    // WS<N>ActiveModules will consume in Slices B-D.  In Slice A the
    // accordion continues to route future / skipped / ended-by-closure
    // to their existing dedicated branches (FutureWorkstagePreview / etc.),
    // so `mode` is threaded but not user-visible yet.  Skipped and
    // ended-by-closure are treated as `future` for the module-render
    // contract (locked, read-only, no historical data) — their
    // status-specific messaging is retained above the module surface.
    const stageMode = useCallback((num) => {
        const s = stageUiStatus(num);
        if (s === "active") return "active";
        if (s === "completed") return "completed";
        return "future";  // future, on-hold, skipped, ended-by-closure
    }, [stageUiStatus]);

    const stageLabel = (num) => {
        const st = authoritativeStates[num];
        const projState = ws?.project_state || "Active";
        if (st === "Active" && projState === "On Hold") return "On Hold";
        if (st === "Active" && projState === "Closed") return "Ended by Project Closure";
        if (st === "Active") return "Active";
        if (st === "Completed") return "Completed";
        if (st === "Skipped / Not Entered") return "Skipped";
        if (st === "Ended by Project Closure") return "Ended by Project Closure";
        return "Future · View only";
    };

    const onAddressSaved = ({ property_address, record_version }) => {
        setWs(prev => prev ? { ...prev, property_address, record_version } : prev);
    };

    // Two-client model — unified field-save handler used by both Client
    // 01 and Client 02 blocks (plan §7.3).  The per-field PATCH returns
    // the full `clients` array + bumped `record_version`.  We also
    // mirror into legacy `client_name` / `email` / `phone` so other
    // call sites (title bar, admin lists) that still read legacy roots
    // get fresh values on the next render.
    const onClientFieldSaved = ({ clients, record_version }) => {
        setWs(prev => {
            if (!prev) return prev;
            const next = { ...prev, clients: clients || [], record_version };
            const c0 = (clients && clients[0]) || null;
            if (c0) {
                const fn = (c0.first_name || "").trim();
                const ln = (c0.last_name || "").trim();
                const display = (fn + " " + ln).trim();
                next.client_name = display || null;
                if (c0.email !== undefined) next.email = c0.email;
                if (c0.phone !== undefined) next.phone = c0.phone;
            }
            return next;
        });
    };

    const [clientAddBusy, setClientAddBusy] = useState(false);
    const [clientRemoveBusy, setClientRemoveBusy] = useState(false);

    const onAddSecondClient = async () => {
        if (!ws || clientAddBusy) return;
        setClientAddBusy(true);
        try {
            const r = await api.post(
                `/admin/projects/${ws.id}/clients`,
                {
                    expected_record_version: ws.record_version || 0,
                    create_op_id: `c-add-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    first_name: null, last_name: null, email: null, phone: null,
                },
            );
            onClientFieldSaved({
                clients: r.data?.clients || [],
                record_version: r.data?.record_version ?? ((ws.record_version || 0) + 1),
            });
        } catch (e) {
            toast.error("Could not add Client 02.");
        } finally {
            setClientAddBusy(false);
        }
    };

    const onRemoveClient = async () => {
        if (!ws || clientRemoveBusy) return;
        setClientRemoveBusy(true);
        try {
            const r = await api.request({
                method: "DELETE",
                url: `/admin/projects/${ws.id}/clients/1`,
                data: {
                    expected_record_version: ws.record_version || 0,
                    create_op_id: `c-del-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                },
            });
            onClientFieldSaved({
                clients: r.data?.clients || [],
                record_version: r.data?.record_version ?? ((ws.record_version || 0) + 1),
            });
        } catch (e) {
            toast.error("Could not remove Client 02.");
        } finally {
            setClientRemoveBusy(false);
        }
    };

    const backToProjects = () => navigate("/admin/projects");

    if (loading) {
        return (
            <div className="text-[#666]" data-testid="workspace-loading">Loading workspace…</div>
        );
    }
    if (error) {
        return (
            <div className="flex flex-col gap-4" data-testid="workspace-error">
                <button onClick={backToProjects} className="inline-flex items-center gap-1.5 text-[13px] text-[#666] hover:text-charcoal" data-testid="workspace-back-error">
                    <ArrowLeft className="w-4 h-4" /> Back to projects
                </button>
                <div className="bg-white border border-[#E5E2DC] rounded-lg p-6">
                    <div className="font-manrope font-semibold text-charcoal mb-1">{error}</div>
                    <div className="text-[13px] text-[#666]">The requested project could not be loaded. Return to Projects and try again.</div>
                </div>
            </div>
        );
    }

    return (
        <TooltipProvider delayDuration={200}>
        <div className="flex flex-col gap-4" data-testid="workspace-page">
            {/* Back to Projects — §5 (return target is Projects, not the
                legacy Enquiries landing). */}
            <button
                type="button"
                onClick={backToProjects}
                data-testid="workspace-back-to-projects"
                className="self-start inline-flex items-center gap-1.5 text-[13px] text-[#666] hover:text-charcoal">
                <ArrowLeft className="w-4 h-4" /> Back to projects
            </button>

            {/* Header §6 — client identity + operational context tags */}
            <header className="flex flex-col gap-3" data-testid="workspace-header">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <h1 className="font-manrope text-3xl font-semibold text-charcoal" data-testid="workspace-client-name">
                        {ws.client_name || "Unnamed project"}
                    </h1>
                    {ws.attention_summary && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#FDF4F2] text-terracotta text-[12px] font-manrope font-semibold" data-testid="workspace-attention-indicator">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            Unresolved attention
                        </span>
                    )}
                </div>
                {/* Public Project ID — secondary, sits directly under
                    the client name.  Uses the authoritative
                    `project_ref`; no client-side derivation. */}
                <div className="flex items-center gap-1.5 text-[12.5px] text-[#666] -mt-1"
                     data-testid="workspace-header-project-id">
                    <span className="font-manrope font-semibold uppercase tracking-wide text-[10.5px]">
                        Project ID
                    </span>
                    <span className="font-mono text-charcoal break-all"
                          data-testid="workspace-header-project-id-value">
                        {ws.project_ref || <span className="text-[#999]">—</span>}
                    </span>
                    {ws.project_ref && (
                        <CopyButton
                            value={ws.project_ref}
                            label="Copy Project ID"
                            testid="workspace-header-project-id-copy" />
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <HeaderTag
                        label="Current Workstage"
                        value={`${ws.workstage}. ${(WORKSTAGES.find(w => w.num === ws.workstage) || {}).name || "—"}`}
                    />
                    <span data-testid="workspace-header-tag-project-state" className="inline-flex items-center gap-1.5 text-[13px]">
                        <span className="px-2 py-0.5 rounded bg-[#FDF4F2] text-terracotta text-[11px] font-manrope font-semibold uppercase tracking-wide">Project State</span>
                        <StateBadge state={ws.project_state || "Active"} />
                    </span>
                    <HeaderTag label="Assigned Owner" value={ws.owner_display_name || ws.owner_id} />
                    <HeaderTag
                        label="Next Action"
                        value={renderNextAction(ws.next_action) || "—"}
                    />
                </div>
            </header>

            {/* Workstage progress ribbon §1 — orientation only; state
                derived from authoritative `workstage_states` (§2). */}
            <WorkstageRibbon workstageStates={ws.workstage_states || []} projectState={ws.project_state || "Active"} />

            {/* Two-column layout §8 — main dominant, persistent rail
                on the right. 70/30-ish ratio at normal laptop widths. */}
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start" data-testid="workspace-two-column">

                {/* LEFT — Workstage accordion stack §9. Primary modules
                    within the Active accordion are FULL-WIDTH stacked
                    vertically §10. */}
                <div className="flex flex-col gap-3 min-w-0" data-testid="workspace-accordion-stack">
                    {/* R2 UI follow-up — bulk toggles for the workstage
                        stack.  Restrained charcoal styling; each button
                        operates on all seven workstages regardless of
                        status. */}
                    <div className="flex justify-end gap-2 -mb-1">
                        <button
                            type="button"
                            onClick={() => setExpandedWs(new Set(WORKSTAGES.map(w => w.num)))}
                            data-testid="ws-expand-all"
                            className="text-[12px] text-charcoal underline underline-offset-2 hover:opacity-80">
                            Expand all
                        </button>
                        <span className="text-[12px] text-[#999]">·</span>
                        <button
                            type="button"
                            onClick={() => setExpandedWs(new Set())}
                            data-testid="ws-collapse-all"
                            className="text-[12px] text-charcoal underline underline-offset-2 hover:opacity-80">
                            Collapse all
                        </button>
                    </div>
                    {WORKSTAGES.map(({ num, name }) => {
                        const status = stageUiStatus(num);
                        const expanded = expandedWs.has(num);
                        return (
                            <WorkstageAccordion
                                key={num}
                                ws={num}
                                name={name}
                                status={status}
                                statusLabel={stageLabel(num)}
                                expanded={expanded}
                                onToggle={() => setExpandedWs(cur => {
                                    // R2 UI follow-up — multi-open:
                                    // toggling one accordion never
                                    // affects the others.
                                    const next = new Set(cur);
                                    if (next.has(num)) next.delete(num);
                                    else next.add(num);
                                    return next;
                                })}>
                                {status === "active" && (
                                    <>
                                        {num === 1 ? (
                                            /* D5 Errata 3a — WS1 renders
                                               inline symmetrically with
                                               WS2–WS7 via the shared
                                               `WS1ActiveModules` component
                                               (B4Pages).  Same visual
                                               language.  The dedicated
                                               `/admin/projects/:id/ws1`
                                               route remains as a thin
                                               wrapper for existing deep
                                               links. */
                                            <WS1ActiveModules
                                                projectId={ws.id}
                                                mode={stageMode(num)}
                                                onCompleted={() => refresh()}
                                            />
                                        ) : num === 2 ? (
                                            /* § correction §1 — WS2
                                               renders inline inside the
                                               canonical Project Workspace
                                               active accordion (not a
                                               separate /ws2 page). */
                                            <WS2ActiveModules
                                                projectId={ws.id}
                                                mode={stageMode(num)}
                                                onCompleted={() => refresh()}
                                            />
                                        ) : num === 3 ? (
                                            /* B6 — WS3 renders inline
                                               inside the canonical
                                               workspace accordion when
                                               Active. */
                                            <WS3ActiveModules
                                                projectId={ws.id}
                                                mode={stageMode(num)}
                                                onCompleted={() => refresh()}
                                            />
                                        ) : num === 4 ? (
                                            /* B7a — WS4 admin foundation. */
                                            <WS4ActiveModules
                                                projectId={ws.id}
                                                mode={stageMode(num)}
                                                onCompleted={() => refresh()}
                                            />
                                        ) : num === 5 ? (
                                            /* B8 — WS5 Project Transition. */
                                            <WS5ActiveModules
                                                projectId={ws.id}
                                                mode={stageMode(num)}
                                                onCompleted={() => refresh()}
                                            />
                                        ) : num === 6 ? (
                                            /* B9/B10 — WS6 Paid Services. */
                                            <WS6ActiveModules
                                                projectId={ws.id}
                                                mode={stageMode(num)}
                                            />
                                        ) : num === 7 ? (
                                            /* B13 — WS7 Post Completion.
                                               Post-V2 cleanup §2: render
                                               the accepted implementation
                                               inline; do NOT show any
                                               "future phase" placeholder
                                               for a Workstage that is
                                               already implemented. */
                                            <PostCompletionPage projectId={ws.id} mode={stageMode(num)} />
                                        ) : (
                                            <>
                                                {/* §11 — B2 accordion is
                                                    the shell for
                                                    Workstages that don't
                                                    yet have an inline
                                                    active-modules
                                                    renderer.  WS1..WS7
                                                    all currently have one
                                                    (WS1 via B4, WS2 via
                                                    B5, …), so this
                                                    fallback is never
                                                    reached at runtime;
                                                    retained for future
                                                    workstages. */}
                                                <ModulePlaceholder
                                                    testid={`ws-${num}-module-placeholder-1`}
                                                    title={`Workstage ${num} modules`}
                                                    description={`Workstage ${num} operational modules will be implemented in a future phase.`} />
                                                <ModulePlaceholder
                                                    testid={`ws-${num}-module-placeholder-2`}
                                                    title="Workstage Completion"
                                                    description="Completion rules and Complete Workstage control land with the Workstage's operational phase, not the shell." />
                                            </>
                                        )}
                                    </>
                                )}
                                {status === "completed" && (
                                    /* D5 Slice E — WS1..WS7 all render
                                       the real module surface in completed
                                       mode via the Slice A read/write
                                       split (historical projection). The
                                       generic completed-summary
                                       placeholder is superseded per
                                       Strategy §14. */
                                    (num === 1 ? (
                                        <WS1ActiveModules projectId={ws.id} mode="completed" />
                                    ) : num === 2 ? (
                                        <WS2ActiveModules projectId={ws.id} mode="completed" />
                                    ) : num === 3 ? (
                                        <WS3ActiveModules projectId={ws.id} mode="completed" />
                                    ) : num === 4 ? (
                                        <WS4ActiveModules projectId={ws.id} mode="completed" />
                                    ) : num === 5 ? (
                                        <WS5ActiveModules projectId={ws.id} mode="completed" />
                                    ) : num === 6 ? (
                                        <WS6ActiveModules projectId={ws.id} mode="completed" />
                                    ) : (
                                        <PostCompletionPage projectId={ws.id} mode="completed" />
                                    ))
                                )}
                                {status === "skipped" && (
                                    <div className="text-[13px] text-[#7A6A50] italic" data-testid={`ws-${num}-skipped-summary`}>
                                        This Workstage was skipped or not entered — no completion is implied.
                                    </div>
                                )}
                                {status === "ended-by-closure" && (
                                    <div className="text-[13px] text-[#7A5148]" data-testid={`ws-${num}-ended-summary`}>
                                        Ended by Project Closure — distinct from Completed.
                                    </div>
                                )}
                                {status === "future" && (
                                    /* D5 Slice E — WS1..WS7 all render
                                       the real module surface in future
                                       mode via the Slice A read/write
                                       split.  The old
                                       `FutureWorkstagePreview` textual
                                       preview is removed. */
                                    (num === 1 ? (
                                        <WS1ActiveModules projectId={ws.id} mode="future" />
                                    ) : num === 2 ? (
                                        <WS2ActiveModules projectId={ws.id} mode="future" />
                                    ) : num === 3 ? (
                                        <WS3ActiveModules projectId={ws.id} mode="future" />
                                    ) : num === 4 ? (
                                        <WS4ActiveModules projectId={ws.id} mode="future" />
                                    ) : num === 5 ? (
                                        <WS5ActiveModules projectId={ws.id} mode="future" />
                                    ) : num === 6 ? (
                                        <WS6ActiveModules projectId={ws.id} mode="future" />
                                    ) : (
                                        <PostCompletionPage projectId={ws.id} mode="future" />
                                    ))
                                )}
                            </WorkstageAccordion>
                        );
                    })}
                </div>

                {/* RIGHT — Persistent contextual rail (V1 §3 order).
                    Locked order: Project Summary → Attention Needed →
                    Actions → Communications.  §3 explicitly forbids
                    dynamic reordering.  The former separate B14
                    Project State rail has been consolidated into the
                    single canonical Actions panel (V1 §2). */}
                <aside
                    className="flex flex-col gap-4 min-w-0"
                    data-testid="workspace-right-rail">
                    <ProjectSummaryPanel
                        ws={ws}
                        onViewFull={() => setShowFullDetails(true)}
                        onAddressSaved={onAddressSaved}
                        onClientFieldSaved={onClientFieldSaved}
                        onAddSecondClient={onAddSecondClient}
                        onRemoveClient={onRemoveClient}
                        clientAddBusy={clientAddBusy}
                        clientRemoveBusy={clientRemoveBusy}
                        onStateChanged={refresh}
                    />
                    <AttentionNeededPanel items={ws.attention_items} projectId={ws.id} />
                    <ActionsPanel projectId={ws.id} currentWorkstage={ws.workstage ?? ws.current_workstage} actionCounts={actionCounts} onStateChanged={refresh} />
                    <CommunicationsPanel items={ws.communications} projectId={ws.id} refreshWorkspace={refresh} />
                </aside>
            </div>

            <ViewFullDetailsDrawer
                open={showFullDetails}
                onClose={() => setShowFullDetails(false)}
                ws={ws}
            />
        </div>
        </TooltipProvider>
    );
}
